import { createClient } from '@supabase/supabase-js';

// Validar variables de entorno críticas de Supabase
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseKey) {
    console.error('❌ Error de configuración: Faltan variables de entorno de Supabase');
    console.error('REACT_APP_SUPABASE_URL:', supabaseUrl ? '✅' : '❌');
    console.error('REACT_APP_SUPABASE_ANON_KEY:', supabaseKey ? '✅' : '❌');
    console.error('⚠️  IMPORTANTE: Usa la ANON KEY (pública), NUNCA la SERVICE_ROLE_KEY');
}

// ⚠️ SEGURIDAD: Verificar que NO se está usando la SERVICE_ROLE_KEY por error
if ((supabaseKey && supabaseKey.includes('service_role')) || supabaseKey?.startsWith('eyJ')) {
    // La SERVICE_ROLE_KEY generalmente empieza con 'eyJ' (JWT) y es más larga
    // La ANON_KEY también puede empezar con 'eyJ', pero podemos verificar la longitud
    if (supabaseKey.length > 200) {
        console.warn('⚠️  ADVERTENCIA: Parece que estás usando una clave muy larga. Verifica que sea la ANON_KEY, no la SERVICE_ROLE_KEY');
    }
}

export const supabase = createClient(supabaseUrl, supabaseKey);

// Función para guardar cliente en la base de datos
export const saveClientToDatabase = async (userData) => {
  try {
    // Usar name si está disponible, si no usar displayName
    // Esto asegura que tanto Google (displayName) como email/contraseña (name) funcionen
    const clientName = userData.name || userData.displayName || null;
    
    console.log('Guardando cliente en Supabase:', {
      email: userData.email,
      name: clientName,
      tieneName: !!userData.name,
      tieneDisplayName: !!userData.displayName
    });
    
    const { data, error } = await supabase
      .from('clients')
      .insert([
        {
          name: clientName, // Se guarda el nombre en el campo 'name' de Supabase
          email: userData.email,
          password: userData.uid || null,
          phone_number: null,
          age: null,
          income: null,
          savings: null,
          debts: null,
          employment_type: null,
          employment_years: null,
          purchase_type: null 
        }
      ])
      .select();

    if (error) {
      console.error('Error al guardar cliente en Supabase:', error);
      throw error;
    }

    console.log('Cliente guardado exitosamente:', data);
    return data;
  } catch (error) {
    console.error('Error en saveClientToDatabase:', error);
    throw error;
  }
};

// Función para verificar si el cliente ya existe
export const checkClientExists = async (email) => {
  try {
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .eq('email', email)
      .single();

    if (error && error.code !== 'PGRST116') {
      throw error;
    }

    return data;
  } catch (error) {
    return null;
  }
};

// Función para obtener la información completa del cliente
export const getClientInfo = async (email) => {
  try {
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .eq('email', email)
      .single();

    if (error && error.code !== 'PGRST116') {
      throw error;
    }

    return data;
  } catch (error) {
    return null;
  }
};

// Función para actualizar información del cliente
export const updateClientInfo = async (email, updateData) => {
  try {
    const { data, error } = await supabase
      .from('clients')
      .update(updateData)
      .eq('email', email)
      .select();

    if (error) {
      throw error;
    }

    return data;
  } catch (error) {
    throw error;
  }
};

// ===== FUNCIONES PARA FAVORITOS =====

// Función para añadir una propiedad a favoritos
export const addToFavorites = async (userId, propertyData) => {
  try {
    const { data, error } = await supabase
      .from('favorites')
      .insert([
        {
          user_id: userId,
          property_code: propertyData.propertyCode || propertyData.url,
          property_url: propertyData.url,
          property_title: propertyData.suggestedTexts?.title || 'Propiedad',
          property_price: propertyData.priceInfo?.price?.amount,
          property_location: propertyData.address,
          property_image: propertyData.thumbnail,
          property_details: {
            rooms: propertyData.rooms,
            bathrooms: propertyData.bathrooms,
            size: propertyData.size,
            floor: propertyData.floor,
            features: propertyData.features
          },
          added_at: new Date().toISOString()
        }
      ])
      .select();

    if (error) {
      throw error;
    }

    return data;
  } catch (error) {
    throw error;
  }
};

// Función para añadir manualmente una propiedad a favoritos
export const addManualFavorite = async (userId, { url, title, price }) => {
  try {
    const { data, error } = await supabase
      .from('favorites')
      .insert([
        {
          user_id: userId,
          property_code: url,
          property_url: url,
          property_title: title || 'Propiedad',
          property_price: price != null ? price : null,
          added_at: new Date().toISOString(),
        },
      ])
      .select();

    if (error) {
      throw error;
    }

    return data;
  } catch (error) {
    throw error;
  }
};

// Función para remover una propiedad de favoritos
export const removeFromFavorites = async (userId, propertyCode) => {
  try {
    const { error } = await supabase
      .from('favorites')
      .delete()
      .eq('user_id', userId)
      .eq('property_code', propertyCode);

    if (error) {
      throw error;
    }

    return true;
  } catch (error) {
    throw error;
  }
};

// Función para obtener todos los favoritos de un usuario
export const getUserFavorites = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('favorites')
      .select('*')
      .eq('user_id', userId)
      .order('added_at', { ascending: false });

    if (error) {
      throw error;
    }

    return data;
  } catch (error) {
    throw error;
  }
};

// Función para verificar si una propiedad está en favoritos
export const isPropertyFavorite = async (userId, propertyCode) => {
  try {
    const { data, error } = await supabase
      .from('favorites')
      .select('id')
      .eq('user_id', userId)
      .eq('property_code', propertyCode)
      .single();

    if (error && error.code !== 'PGRST116') {
      throw error;
    }

    return !!data; // Retorna true si existe, false si no
  } catch (error) {
    return false;
  }
};

// ===== FUNCIONES PARA CHATS GUARDADOS =====

// Función para guardar un chat
export const saveChat = async (userId, chatName, messages, sessionId) => {
  try {
    // 1) Intentar encontrar un chat existente por (user_id, session_id)
    //    para evitar duplicados si se disparan dos guardados casi a la vez
    const existing = await supabase
      .from('saved_chats')
      .select('*')
      .eq('user_id', userId)
      .eq('session_id', sessionId)
      .order('created_at', { ascending: false })
      .limit(1);

    if (existing && existing.data && existing.data.length > 0) {
      const existingChat = existing.data[0];
      // Actualizar el existente en lugar de crear uno nuevo
      const { data: updated, error: updateError } = await supabase
        .from('saved_chats')
        .update({
          chat_name: chatName,
          messages: messages,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .eq('id', existingChat.id)
        .select();

      if (updateError) {
        throw updateError;
      }

      return updated ? updated[0] : existingChat;
    }

    // 2) Si no existe uno con ese sessionId, crear uno nuevo
    const { data, error } = await supabase
      .from('saved_chats')
      .insert([
        {
          user_id: userId,
          chat_name: chatName,
          messages: messages,
          session_id: sessionId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
      ])
      .select();

    if (error) {
      throw error;
    }

    return data[0];
  } catch (error) {
    throw error;
  }
};

// Función para obtener todos los chats guardados de un usuario
export const getUserSavedChats = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('saved_chats')
      .select('*')
      .eq('user_id', userId)
      .order('updated_at', { ascending: false });

    if (error) {
      throw error;
    }

    return data || [];
  } catch (error) {
    return [];
  }
};

// Función para cargar un chat específico
export const loadChat = async (userId, chatId) => {
  try {
    const { data, error } = await supabase
      .from('saved_chats')
      .select('*')
      .eq('user_id', userId)
      .eq('id', chatId)
      .single();

    if (error) {
      throw error;
    }

    return data;
  } catch (error) {
    return null;
  }
};

// Función para actualizar un chat existente
export const updateChat = async (userId, chatId, messages) => {
  try {
    const { data, error } = await supabase
      .from('saved_chats')
      .update({
        messages: messages,
        updated_at: new Date().toISOString()
      })
      .eq('user_id', userId)
      .eq('id', chatId)
      .select();

    if (error) {
      throw error;
    }

    return data[0];
  } catch (error) {
    throw error;
  }
};

// Función para eliminar un chat guardado
export const deleteChat = async (userId, chatId) => {
  try {
    const { error } = await supabase
      .from('saved_chats')
      .delete()
      .eq('user_id', userId)
      .eq('id', chatId);

    if (error) {
      throw error;
    }

    return true;
  } catch (error) {
    throw error;
  }
};

// Función para renombrar un chat
export const renameChat = async (userId, chatId, newName) => {
  try {
    const { data, error } = await supabase
      .from('saved_chats')
      .update({
        chat_name: newName,
        updated_at: new Date().toISOString()
      })
      .eq('user_id', userId)
      .eq('id', chatId)
      .select();

    if (error) {
      throw error;
    }

    return data[0];
  } catch (error) {
    throw error;
  }
};

// ===== FUNCIONES PARA ACEPTACIÓN DE TÉRMINOS =====

// Función para verificar si el usuario ha aceptado los términos
export const hasAcceptedTerms = async (email) => {
  try {
    const { data, error } = await supabase
      .from('clients')
      .select('terms_accepted')
      .eq('email', email)
      .single();

    if (error && error.code !== 'PGRST116') {
      throw error;
    }

    // Si no existe el usuario o no tiene el campo, retornar false
    return data?.terms_accepted === true;
  } catch (error) {
    return false;
  }
};

// Función para actualizar la aceptación de términos
export const updateClientTermsAcceptance = async (email, accepted) => {
  try {
    // Primero verificar si el usuario existe
    const existingClient = await checkClientExists(email);
    
    if (!existingClient) {
      throw new Error('Usuario no encontrado. Por favor, inicia sesión nuevamente.');
    }

    // Intentar actualizar los campos de términos
    // Si los campos no existen, intentar actualizar solo los campos que sí existen
    
    // Intentar actualizar terms_accepted si el campo existe
    try {
      const { data, error } = await supabase
        .from('clients')
        .update({
          terms_accepted: accepted,
          terms_accepted_at: accepted ? new Date().toISOString() : null
        })
        .eq('email', email)
        .select();

      if (error) {
        // Si el error es porque los campos no existen, intentar solo con un campo alternativo
        if (error.message && error.message.includes('column') && error.message.includes('does not exist')) {
          throw new Error('Los campos de términos no están configurados en la base de datos. Contacta al administrador.');
        }
        throw error;
      }

      return data[0];
    } catch (updateError) {
      throw updateError;
    }
  } catch (error) {
    throw error;
  }
};

// Función para actualizar el tipo de compra (solo/pareja)
export const updateClientPurchaseType = async (email, purchaseType) => {
  try {
    // Verificar si el usuario existe
    const existingClient = await checkClientExists(email);
    
    if (!existingClient) {
      throw new Error('Usuario no encontrado. Por favor, inicia sesión nuevamente.');
    }

    // Actualizar purchase_type
    const { data, error } = await supabase
      .from('clients')
      .update({
        purchase_type: purchaseType // 'solo' o 'pareja'
      })
      .eq('email', email)
      .select();

    if (error) {
      // Si el error es porque el campo no existe
      if (error.message && error.message.includes('column') && error.message.includes('does not exist')) {
        throw new Error('El campo purchase_type no está configurado en la base de datos. Contacta al administrador.');
      }
      throw error;
    }

    return data[0];
  } catch (error) {
    throw error;
  }
};

// Función para obtener el tipo de compra del usuario
export const getClientPurchaseType = async (email) => {
  try {
    const { data, error } = await supabase
      .from('clients')
      .select('purchase_type')
      .eq('email', email)
      .single();

    if (error && error.code !== 'PGRST116') {
      throw error;
    }

    return data?.purchase_type || null;
  } catch (error) {
    return null;
  }
};

// ===== FUNCIONES PARA REPORTES PDF =====

// Función para obtener todos los reportes PDF de un usuario
export const getUserPropertyReports = async (userEmail) => {
  try {
    const { data, error } = await supabase
      .from('property_reports')
      .select('*')
      .eq('user_email', userEmail)
      .order('created_at', { ascending: false });

    if (error) {
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Error obteniendo reportes:', error);
    return [];
  }
};

// Función para obtener la URL pública de un PDF desde Supabase Storage
export const getReportPdfUrl = (pdfPath, pdfUrl) => {
  // Si ya tenemos una URL completa, usarla
  if (pdfUrl && (pdfUrl.startsWith('http://') || pdfUrl.startsWith('https://'))) {
    return pdfUrl;
  }
  
  // Si tenemos un pdfPath, construir la URL
  if (pdfPath) {
    // Si ya es una URL completa, retornarla
    if (pdfPath.startsWith('http://') || pdfPath.startsWith('https://')) {
      return pdfPath;
    }
    
    // Si es una ruta de Supabase Storage, construir la URL pública
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    if (!supabaseUrl) return null;
    
    // El pdfPath puede venir como "user-email/filename.pdf" o ya incluir el bucket
    let storagePath = pdfPath;
    if (!storagePath.includes('property-reports/')) {
      storagePath = `property-reports/${storagePath}`;
    }
    
    const storageUrl = `${supabaseUrl}/storage/v1/object/public/${storagePath}`;
    return storageUrl;
  }
  
  return null;
};
